import React, {createContext, useContext, useReducer, useMemo, useCallback} from 'react';
import {validateFloorInfo, validateFloorArea, checkMaxCapacityAvailability} from './utils';

import useSafeDispatch from 'hooks/useSafeDispatch';

import {actionTypes, facilitiesReducer, initialState} from './facilitiesReducer';

const FacilitiesContext = createContext();
FacilitiesContext.displayName = 'FacilitiesContext';

const FacilitiesDispatch = createContext();
FacilitiesDispatch.displayName = 'FacilitiesDispatch';

const FacilitiesProvider = ({children, reducer = facilitiesReducer}) => {
  const [state, unsafeDispatch] = useReducer(reducer, initialState);

  const memoizedState = useMemo(() => state, [state]);
  const dispatch = useSafeDispatch(unsafeDispatch);

  return (
    <FacilitiesContext.Provider value={memoizedState}>
      <FacilitiesDispatch.Provider value={{dispatch}}>{children}</FacilitiesDispatch.Provider>
    </FacilitiesContext.Provider>
  );
};

function useFacilitiesState() {
  const context = useContext(FacilitiesContext);

  if (context === undefined) {
    throw new Error('useFacilitiesState must be used within a FacilitiesProvider');
  }

  const {floorInfo, floorAreas, floorSeats} = context;
  const floorInfoIsValid = validateFloorInfo(floorInfo);
  const floorAreasIsValid = validateFloorArea(floorAreas);
  const floorSeatsIsValid = floorSeats.positions.length > 0;
  const maxCapacityAvailability = checkMaxCapacityAvailability(
    floorSeats.positions,
    floorInfo.maxCapacity
  );

  return {
    ...context,
    floorInfoIsValid,
    floorAreasIsValid,
    floorSeatsIsValid,
    maxCapacityAvailability
  };
}

function useFacilitiesDispatch() {
  const context = useContext(FacilitiesDispatch);

  if (context === undefined) {
    throw new Error('useFacilitiesDispatch must be used within a FacilitiesProvider');
  }

  const {dispatch} = context;

  const setActiveStepNum = useCallback(
    (payload) => dispatch({type: actionTypes.setActiveStepNum, payload}),
    [dispatch]
  );

  const setFloorInfo = useCallback(
    (payload) => dispatch({type: actionTypes.setFloorInfo, payload}),
    [dispatch]
  );

  const setFloorPlanImage = useCallback(
    (payload) => dispatch({type: actionTypes.setFloorPlanImage, payload}),
    [dispatch]
  );

  const toggleMultipleAreas = useCallback(() => dispatch({type: actionTypes.toggleMultipleAreas}), [
    dispatch
  ]);

  const addAreaAssignment = useCallback(
    (payload) => dispatch({type: actionTypes.addAreaAssignment, payload}),
    [dispatch]
  );

  const removeAreaAssignment = useCallback(
    (payload) => dispatch({type: actionTypes.removeAreaAssignment, payload}),
    [dispatch]
  );

  const setWholeAreaAssignmentValues = useCallback(
    (payload) => dispatch({type: actionTypes.setWholeAreaAssignmentValues, payload}),
    [dispatch]
  );

  const setSeatPositions = useCallback(
    (payload) => dispatch({type: actionTypes.setSeatPositions, payload}),
    [dispatch]
  );

  const setFacilityDetails = useCallback(
    (payload) => dispatch({type: actionTypes.setFacilityDetails, payload}),
    [dispatch]
  );

  return {
    dispatch,
    setActiveStepNum,
    setFloorInfo,
    setFloorPlanImage,
    toggleMultipleAreas,
    addAreaAssignment,
    removeAreaAssignment,
    setWholeAreaAssignmentValues,
    setSeatPositions,
    setFacilityDetails
  };
}

export {FacilitiesProvider, useFacilitiesState, useFacilitiesDispatch};
